import React, { useCallback, useContext, useEffect, useState } from 'react';
import cn from 'classnames';
import FocusTrap from 'focus-trap-react';
import { ThemeOverride, ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import { PapiUser } from '@wix/da-papi-types';
import { DropdownStateContext } from '@wix/da-ds/pkg/Dropdown/DropdownStateManager';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { useFriendList } from '../../utils/hooks/useFriendList';
import { useListKeyboardNavigation } from '../../utils/hooks/useListKeyboardNavigation';
import UserInput from '../_partials/UserInput';
import UserList from '../_partials/UserList';
import s from './DeviantsListCard.scss';

export interface Props {
  className?: string;
  contentClassName?: string;
  selectedUserId?: number;
  deviantsStreamId?: string;
  onItemClick?: (user: PapiUser) => void;
  itemSuffix?: React.ReactNode;
  itemBiData?: any;
  emptyView?: React.ReactNode;
  showSuffixOnlyOnHover?: boolean;
  onClose?: () => void;
}

const DeviantsListCard: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  contentClassName,
  selectedUserId,
  deviantsStreamId,
  itemSuffix,
  itemBiData,
  showSuffixOnlyOnHover,
  emptyView,
  onItemClick,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { closeDropdown } = useContext(DropdownStateContext);
  const { users, streamId, isLoading, isInitialized } = useFriendList(searchTerm, deviantsStreamId);
  const onKeyboardItemSelected = useCallback(
    (selectedIndex: number) =>
      onItemClick && users[selectedIndex] && onItemClick(users[selectedIndex]),
    [onItemClick, users]
  );
  const { onKeyEvent, selectedIndex, resetSelectedIndex, selectFirstIndex } =
    useListKeyboardNavigation(users.length, onKeyboardItemSelected, onClose ?? closeDropdown);

  // Reset the selected index to nothing when our list is loading,
  // or to the first item if the user is actively searching.
  useEffect(() => {
    if (isLoading) {
      resetSelectedIndex();
    } else if (!isLoading && isInitialized && searchTerm.length >= 3) {
      selectFirstIndex();
    }
  }, [isLoading, isInitialized, resetSelectedIndex, searchTerm, selectFirstIndex]);

  // For use with aria-activedescendant
  const generateItemId = useCallback(index => `deviation-list-item-${index}`, []);

  return (
    <div className={cn('ds-surface-tertiary', s['root'], className)}>
      <ThemeOverride className={s['theme-wrapper']} themeSurface={ThemeSurface.TERTIARY}>
        <FocusTrap focusTrapOptions={{ clickOutsideDeactivates: true }}>
          <div
            className={cn(s['content'], s['inputs-updated'], contentClassName)}
            onKeyDown={onKeyEvent}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded="true"
          >
            <div className={s['input-wrapper']}>
              <UserInput
                value={searchTerm}
                onChange={setSearchTerm}
                className={s['filter-input']}
                /*
              The conditional 'none' here is in response to the below part of the WAI-ARIA spec. '>= 3' because we only
              start the search at greater-than-or-equal to 3 characters.

              > The aria-autocomplete property is limited to describing predictive behaviors of an input element.
              > Authors SHOULD [...] set aria-autocomplete to none if an input element provides one or more input proposals
              > where none of the proposals are dependent on the specific input provided by the user.

              https://www.w3.org/TR/wai-aria-1.1/#aria-autocomplete
            */
                aria-autocomplete={searchTerm.length >= 3 ? 'list' : 'none'}
                aria-activedescendant={
                  selectedIndex !== undefined ? generateItemId(selectedIndex) : undefined
                }
                aria-controls="deviants-card-user-list"
              />
            </div>
            {isInitialized && (
              <UserList
                id="deviants-card-user-list"
                itemIdGenerator={generateItemId}
                streamId={streamId}
                users={users}
                className={s['list']}
                isLoading={isLoading}
                selectedUserId={selectedUserId}
                itemSuffix={itemSuffix}
                itemBiData={itemBiData}
                showSuffixOnlyOnHover={showSuffixOnlyOnHover}
                keyboardSelectedIndex={selectedIndex}
                onItemClick={onItemClick}
                emptyView={emptyView}
              />
            )}
            {!isInitialized && <LoadingIndicator />}
          </div>
        </FocusTrap>
      </ThemeOverride>
    </div>
  );
};
DeviantsListCard.displayName = 'DeviantsListCard';

export default DeviantsListCard;
